import React from 'react';
import { Settings as SettingsIcon, Globe, Pill } from 'lucide-react';
import { SettingsSection, type SettingsSectionItem } from '../../libraries/settings/SettingsSection';
import { Tabs } from '~/shared/components/tabs/Tabs';
import { QueryParamTabs } from '~/shared/components/tabs/QueryParamTabs';
import { AgencyStyleSettingsSection } from '~/libraries/agencies/AgencyStyleSettingsForm';
import { ComprehensiveAssessmentAgencySettingsSection } from '~/libraries/agencies/ComprehensiveAssessmentAgencySettingsForm';
import { DisciplineAgencySettingsSection } from '~/libraries/agencies/DisciplineAgencySettingsForm';
import { VisitAgencySettingsSection } from '~/libraries/agencies/VisitAgencySettingsForm';
import { MedicareAgencySettingsSection } from '~/libraries/agencies/MedicareAgencySettingsForm';
import { GeneralAgencySettingsSection } from '~/libraries/agencies/GeneralAgencySettingsForm';
import { PharmacyLibrarySection } from '~/libraries/pharmacies/PharmacyLibrary';
import { PhysicianLibrarySection } from '~/libraries/physicians/PhysicianLibrary';
import { InsuranceProviderLibrarySection } from '~/libraries/insurance-providers/InsuranceProviderLibrary';
import { PhysicianGroupLibrarySection } from '~/libraries/physicians/groups/PhysicianGroupLibrary';
import { FacilityLibrarySection } from '~/libraries/facilities/FacilityLibrary';
import { UserLibrarySection } from '~/libraries/users/UserLibrary';
import { TechnicalAgencySettingsSection } from '~/libraries/agencies/TechnicalAgencySettings';
import { InterventionDefinitionLibrarySection } from '~/libraries/interventions/library/InterventionDefinitionLibrary';
import { PageLayout } from '~/shared/PageLayout';
import { OasisDefinitionLibrarySection } from '~/libraries/oasis/OasisDefinitionLibrary';

const agencySettingsItems = [
	GeneralAgencySettingsSection,
	MedicareAgencySettingsSection,
	DisciplineAgencySettingsSection,
	ComprehensiveAssessmentAgencySettingsSection,
	VisitAgencySettingsSection,
	TechnicalAgencySettingsSection,
	AgencyStyleSettingsSection,
	{
		settingsView: 'frequency-defaults',
		title: 'Frequency Defaults',
		description: 'Set default visit frequencies for various disciplines',
		TitleIcon: Pill
	}
] satisfies SettingsSectionItem[];

const libraryItems = [
	FacilityLibrarySection,
	InsuranceProviderLibrarySection,
	{
		settingsView: 'language',
		title: 'Language Resources',
		description: 'Manage interpreter services and language support',
		TitleIcon: Globe
	},
	PharmacyLibrarySection,
	PhysicianLibrarySection,
	PhysicianGroupLibrarySection,
	UserLibrarySection,
	InterventionDefinitionLibrarySection,
	OasisDefinitionLibrarySection
] satisfies SettingsSectionItem[];

export default function SettingsPage() {
	return (
		<PageLayout
			pageHeader={{
				title: 'Settings',
				description: 'Manage agency settings and library resources',
				TitleIcon: SettingsIcon
			}}
		>
			<QueryParamTabs defaultTab="agency">
				<Tabs.TabList>
					<Tabs.Tab id="agency">Agency Settings</Tabs.Tab>
					<Tabs.Tab id="libraries">Libraries</Tabs.Tab>
				</Tabs.TabList>
				<Tabs.TabPanel id="agency">
					<div className="space-y-6 p-4">
						<div className="mb-6">
							<h2 className="mb-2 text-xl font-semibold text-gray-900">Agency Settings</h2>
							<p className="text-gray-600">Manage agency settings.</p>
						</div>
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
							{agencySettingsItems.map(item => (
								<SettingsSection key={item.settingsView} {...item} routePrefix="agency" />
							))}
						</div>
					</div>
				</Tabs.TabPanel>
				<Tabs.TabPanel id="libraries">
					<div className="space-y-6 p-4">
						<div className="mb-6">
							<h2 className="mb-2 text-xl font-semibold text-gray-900">Reference Libraries</h2>
							<p className="text-gray-600">Manage reference data used throughout the application</p>
						</div>
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
							{libraryItems.map(item => (
								<SettingsSection key={item.settingsView} {...item} routePrefix="libraries" />
							))}
						</div>
					</div>
				</Tabs.TabPanel>
			</QueryParamTabs>
		</PageLayout>
	);
}
